/**
 * ErrorFallback Component
 *
 * Full-page error fallback that uses the AccessError component.
 * Designed for app-level and route-level error boundaries.
 *
 * Features:
 * - Uses the existing AccessError component with zebra mascot
 * - Shows error code and message
 * - Provides navigation back home
 * - Optional retry/reset button
 *
 * @example
 * ```tsx
 * <ErrorBoundary FallbackComponent={ErrorFallback}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */

import type { FC } from "react";
import { Button } from "@/components/ui/buttons/Button";
import { AccessError } from "../AccessError";
import type { ErrorFallbackProps } from "./types";
import styles from "./ErrorFallback.module.css";

export const ErrorFallback: FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  code = "Oops!",
  message,
  homeUrl = "/",
  homeLinkText = "Gå tilbake",
  showReset = true,
  resetButtonText = "Prøv igjen",
}) => {
  // Use custom message or derive from error
  const errorMessage =
    message ||
    (import.meta.env.DEV
      ? error.message
      : "Noe gikk galt. Vennligst prøv igjen senere.");

  return (
    <div className={styles.container} data-testid="error-fallback">
      <AccessError
        code={code}
        message={errorMessage}
        homeUrl={homeUrl}
        homeLinkText={homeLinkText}
        data-testid="error-fallback-access-error"
      />
      {showReset && (
        <div className={styles.resetContainer}>
          <Button
            variant="primary"
            size="md"
            onClick={resetErrorBoundary}
            className={styles.resetButton}
            data-testid="error-fallback-reset-button"
          >
            {resetButtonText}
          </Button>
        </div>
      )}
      {import.meta.env.DEV && (
        <details className={styles.errorDetails} data-testid="error-details">
          <summary>Tekniske detaljer (kun synlig i utvikling)</summary>
          <pre className={styles.errorStack}>{error.stack}</pre>
        </details>
      )}
    </div>
  );
};

ErrorFallback.displayName = "ErrorFallback";
